import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  FiSearch,
  FiFilter,
  FiChevronDown,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiX,
  FiEye,
  FiBarChart2,
  FiSave
} from 'react-icons/fi';
import { GlassCard } from '../../components/ui/GlassCard';
import { LoadingSkeleton } from '../../components/ui/LoadingSkeleton';
import * as celebUpdateService from '../../services/celebUpdateService';
import toast from 'react-hot-toast';

export const AdminCelebUpdates = () => {
  const [celebUpdates, setCelebUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    link: '',
    price: '',
    status: 'active'
  });

  const statusOptions = ['active', 'inactive', 'archived'];

  useEffect(() => {
    fetchCelebUpdates();
  }, []);

  const fetchCelebUpdates = async () => {
    try {
      setIsLoading(true);
      const response = await celebUpdateService.getAdminCelebUpdates();
      setCelebUpdates(response.celebUpdates || []);
    } catch (error) {
      toast.error('Failed to load celeb updates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setFormData({
      title: '',
      description: '',
      link: '',
      price: '',
      status: 'active'
    });
    setIsEditing(false);
    setSelectedUpdate(null);
    setShowCreateModal(true);
  };

  const handleOpenEditModal = (update) => {
    setFormData({
      title: update.title,
      description: update.description,
      link: update.link,
      price: update.price,
      category: update.category,
      status: update.status
    });
    setSelectedUpdate(update);
    setIsEditing(true);
    setShowCreateModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!formData.title || !formData.description || !formData.link || !formData.price) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setIsSaving(true);
      if (isEditing && selectedUpdate) {
        await celebUpdateService.updateCelebUpdate(selectedUpdate._id, formData);
        toast.success('Update edited successfully');
      } else {
        await celebUpdateService.createCelebUpdate(formData);
        toast.success('Update created successfully');
      }
      setShowCreateModal(false);
      fetchCelebUpdates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;

    try {
      await celebUpdateService.deleteCelebUpdate(id);
      toast.success('Update deleted successfully');
      fetchCelebUpdates();
    } catch (error) {
      toast.error('Failed to delete update');
    }
  };

  const getFilteredUpdates = () => {
    let filtered = [...celebUpdates];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter((u) => u.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((u) => u.category === categoryFilter);
    }

    // Search
    if (searchTerm) {
      filtered = filtered.filter(
        (u) =>
          u.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          u.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredUpdates = getFilteredUpdates();

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: { transition: { staggerChildren: 0.05 } }
  };

  return (
    <>
      <Helmet>
        <title>Manage Tools & Updates | Nitro Admin</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-luxury-50 via-white to-luxury-100 py-6 sm:py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          >
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold text-luxury-900">
                Manage <span className="gradient-text">Tools & Updates</span>
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-luxury-600 mt-2">
                Create, edit, and manage tools & updates
              </p>
            </div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleOpenCreateModal}
              className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl flex items-center space-x-2 hover:shadow-lg transition-all text-xs sm:text-sm flex-shrink-0"
            >
              <FiPlus className="w-4 sm:w-5 h-4 sm:h-5" />
              <span>New Update</span>
            </motion.button>
          </motion.div>

          {/* Stats Row */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8"
          >
            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-luxury-500 mb-1">Total Updates</p>
                    <p className="text-2xl sm:text-3xl font-bold text-luxury-900">
                      {celebUpdates.length}
                    </p>
                  </div>
                  <FiBarChart2 className="w-8 sm:w-10 h-8 sm:h-10 text-primary-600" />
                </div>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-luxury-500 mb-1">Active</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600">
                      {celebUpdates.filter((u) => u.status === 'active').length}
                    </p>
                  </div>
                  <FiEye className="w-8 sm:w-10 h-8 sm:h-10 text-green-600" />
                </div>
              </GlassCard>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <GlassCard className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-luxury-500 mb-1">Total Sales</p>
                    <p className="text-2xl sm:text-3xl font-bold gradient-text">
                      {celebUpdates.reduce((sum, u) => sum + u.purchaseCount, 0)}
                    </p>
                  </div>
                  <FiBarChart2 className="w-8 sm:w-10 h-8 sm:h-10 text-orange-600" />
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>

          {/* Filters Bar */}
          <motion.div
            variants={fadeInUp}
            initial="initial"
            animate="animate"
            className="glass rounded-2xl p-4 mb-8"
          >
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-luxury-400 w-4 sm:w-5" />
                <input
                  type="text"
                  placeholder="Search updates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-luxury pl-12 w-full text-xs sm:text-sm"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl flex items-center space-x-2 transition-all text-xs sm:text-sm flex-shrink-0 ${
                  showFilters
                    ? 'bg-primary-600 text-white'
                    : 'glass hover:bg-primary-50 text-luxury-700'
                }`}
              >
                <FiFilter className="w-4 sm:w-5 h-4 sm:h-5" />
                <span>Filters</span>
                <FiChevronDown
                  className={`w-4 h-4 transition-transform duration-300 ${
                    showFilters ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            {/* Filter Panel */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="pt-6 mt-4 border-t border-luxury-200">
                    <div className="grid grid-cols-1 gap-4">
                      {/* Status */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                          Status
                        </label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="input-luxury w-full text-xs sm:text-sm"
                        >
                          <option value="all">All Status</option>
                          {statusOptions.map((status) => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Updates Table */}
          {isLoading ? (
            <LoadingSkeleton count={5} />
          ) : filteredUpdates.length > 0 ? (
            <motion.div
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6"
            >
              {filteredUpdates.map((update) => (
                <motion.div
                  key={update._id}
                  variants={fadeInUp}
                  className="glass p-4 sm:p-6 rounded-2xl border border-luxury-200 hover:shadow-luxury-lg transition-all"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mb-4 pb-4 border-b border-luxury-200">
                    {/* Left Side */}
                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm sm:text-base font-bold text-luxury-900 line-clamp-1">
                            {update.title}
                          </h3>
                          <p className="text-xs text-luxury-600">
                            by {update.addedBy?.username || 'System'}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${
                          update.status === 'active'
                            ? 'bg-green-100 text-green-700'
                            : update.status === 'inactive'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {update.status}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-luxury-600 line-clamp-2 mb-2">
                        {update.description}
                      </p>
                      <span className="text-xs sm:text-sm text-luxury-600">
                        Price: <span className="font-bold gradient-text">₦{update.price.toLocaleString()}</span>
                      </span>
                    </div>

                    {/* Right Side */}
                    <div className="flex flex-col space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <GlassCard className="p-2">
                          <p className="text-xs text-luxury-500">Purchases</p>
                          <p className="text-lg sm:text-xl font-bold text-orange-600">
                            {update.purchaseCount}
                          </p>
                        </GlassCard>
                        <GlassCard className="p-2">
                          <p className="text-xs text-luxury-500">Revenue</p>
                          <p className="text-lg sm:text-xl font-bold gradient-text">
                            ₦{(update.price * update.purchaseCount).toLocaleString()}
                          </p>
                        </GlassCard>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleOpenEditModal(update)}
                      className="px-3 sm:px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs sm:text-sm font-medium flex items-center space-x-1"
                    >
                      <FiEdit className="w-4 h-4" />
                      <span>Edit</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleDelete(update._id)}
                      className="px-3 sm:px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs sm:text-sm font-medium flex items-center space-x-1"
                    >
                      <FiTrash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <GlassCard className="p-8 sm:p-12 text-center">
              <div className="w-16 h-16 bg-luxury-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiSearch className="w-8 h-8 text-luxury-400" />
              </div>
              <p className="text-luxury-600 text-sm mb-4">No updates found</p>
              <button
                onClick={handleOpenCreateModal}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                Create the first update
              </button>
            </GlassCard>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="glass rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg sm:text-xl font-bold text-luxury-900">
                  {isEditing ? 'Edit Tools/Update' : 'Create New Tools/Update'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-luxury-400 hover:text-luxury-600"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleFormChange}
                    className="input-luxury w-full text-xs sm:text-sm"
                    placeholder="Update title"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    className="input-luxury w-full text-xs sm:text-sm resize-none"
                    rows="4"
                    placeholder="Update description"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                    Link *
                  </label>
                  <input
                    type="url"
                    name="link"
                    value={formData.link}
                    onChange={handleFormChange}
                    className="input-luxury w-full text-xs sm:text-sm"
                    placeholder="https://example.com"
                  />
                </div>

<div>
                    <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                      Price (₦) *
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleFormChange}
                      className="input-luxury w-full text-xs sm:text-sm"
                      placeholder="0"
                      min="0"
                    />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-luxury-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    className="input-luxury w-full text-xs sm:text-sm"
                  >
                    {statusOptions.map((status) => (
                      <option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                disabled={isSaving}
                className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 text-white py-3 rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <FiSave className="w-5 h-5" />
                <span>{isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}</span>
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminCelebUpdates;